import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  HERA_COURT_LINK_PUBLIC_INSTALL_HREF,
  HERA_COURT_LINK_USERSCRIPT_HREF,
  HERA_COURT_LINK_ZIP_HREF,
  getHeraCourtLinkChromeWebStoreUrl,
} from "@/lib/court-link-extension-install";
import { Download, Puzzle } from "lucide-react";

interface BrowserExtensionsSetupProps {
  /** Operator setup can show Chrome Web Store publishing notes. */
  showPublishNotes?: boolean;
  /** Override for tests; defaults to the configured store listing URL. */
  chromeWebStoreUrl?: string | null;
}

export function BrowserExtensionsSetup({
  showPublishNotes = false,
  chromeWebStoreUrl = getHeraCourtLinkChromeWebStoreUrl(),
}: BrowserExtensionsSetupProps) {
  return (
    <div className="space-y-6 max-w-3xl">
      <p className="text-sm text-muted-foreground">
        Hera Court Link is a small Chrome helper. After it is installed, case
        numbers configured with the EXTENSION live-link method open the court
        site and jump to the matching search automatically.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Puzzle className="h-5 w-5" aria-hidden />
            Chrome (recommended)
          </CardTitle>
          <CardDescription>
            Private or unlisted Chrome Web Store listing — no Developer mode
            after it is published.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {chromeWebStoreUrl ? (
            <Button asChild>
              <a href={chromeWebStoreUrl} target="_blank" rel="noreferrer">
                Install Hera Court Link from Chrome Web Store
              </a>
            </Button>
          ) : (
            <p className="text-sm">
              The private Chrome listing is not configured in this environment
              yet. Use the package download below, or Tampermonkey, until the
              store link is added.
            </p>
          )}
          {showPublishNotes ? (
            <ol className="list-decimal pl-5 text-sm space-y-2">
              <li>
                Create the listing once in the{" "}
                <a
                  href="https://chrome.google.com/webstore/devconsole"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  Chrome Web Store Developer Dashboard
                </a>{" "}
                as <strong>Unlisted</strong> or <strong>Private</strong>. GitHub
                Actions uploads later versions when the extension changes.
              </li>
              <li>
                Set <code>VITE_HERA_COURT_LINK_CHROME_WEB_STORE_URL</code> to
                the listing URL so the install button appears here.
              </li>
            </ol>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Download package</CardTitle>
          <CardDescription>
            Load unpacked in Chrome while the store listing is unavailable, or
            to test a new build.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button asChild variant="outline">
            <a href={HERA_COURT_LINK_ZIP_HREF} download="hera-court-link.zip">
              <Download className="h-4 w-4" aria-hidden />
              Download Hera Court Link (.zip)
            </a>
          </Button>
          <ol className="list-decimal pl-5 text-sm space-y-2">
            <li>Unzip the downloaded file.</li>
            <li>
              Open <code>chrome://extensions</code> (paste that in the address
              bar).
            </li>
            <li>Turn on Developer mode.</li>
            <li>
              Click <strong>Load unpacked</strong> and select the unzipped
              folder (it contains <code>manifest.json</code>).
            </li>
            <li>Pin Hera Court Link (optional).</li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tampermonkey</CardTitle>
          <CardDescription>
            Use this if your team already runs Tampermonkey. Same helper
            behavior as the Chrome extension.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal pl-5 text-sm space-y-2">
            <li>Install the Tampermonkey browser extension if needed.</li>
            <li>
              In Chrome, open <code>chrome://extensions</code>, click
              Tampermonkey <strong>Details</strong>, and turn on{" "}
              <strong>Allow User Scripts</strong>.
            </li>
            <li>
              Chrome does not reliably intercept <code>.user.js</code> links. In
              Tampermonkey open <strong>Dashboard → Utilities</strong>, paste
              the install URL, and click Install. Use version{" "}
              <strong>1.0.1</strong> or later:
            </li>
          </ol>
          <p className="text-sm">
            <a
              href={HERA_COURT_LINK_USERSCRIPT_HREF}
              className="underline break-all"
              target="_blank"
              rel="noreferrer"
            >
              {HERA_COURT_LINK_USERSCRIPT_HREF}
            </a>
          </p>
          <p className="text-sm text-muted-foreground">
            A shareable install page also lives at{" "}
            <a
              href={HERA_COURT_LINK_PUBLIC_INSTALL_HREF}
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              {HERA_COURT_LINK_PUBLIC_INSTALL_HREF}
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
